import { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminSidebar from '@/components/AdminSidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Menu, ArrowLeft, Loader2, ExternalLink, RefreshCw, ChevronDown, ChevronRight, Upload, Image as ImageIcon, Video, X, Search, FolderOpen } from 'lucide-react';
import { getPageConfig, getExpertiseDetailConfig, type FieldConfig, type SectionConfig } from '@/config/pageConfigs';
import { useCmsContent } from '@/hooks/useCmsContent';
import { updatePageContent } from '@/api/cmsService';
import { getMedia, searchMedia, uploadMedia, type CmsMedia } from '@/api/mediaService';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient, useQuery } from 'react-query';

function MediaPickerModal({ isOpen, onClose, onSelect, mediaType }: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  mediaType: 'image' | 'video';
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: mediaItems = [], isLoading } = useQuery(
    ['media-picker', searchQuery],
    () => searchQuery ? searchMedia(searchQuery) : getMedia(),
    { enabled: isOpen, keepPreviousData: true }
  );

  const filtered = mediaItems.filter((item) =>
    mediaType === 'video'
      ? item.mimeType?.startsWith('video/')
      : item.mimeType?.startsWith('image/') || item.fileType === 'image'
  );

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    try {
      const file = files[0];
      if (!file) return;
      const result = await uploadMedia(file);
      queryClient.invalidateQueries('media-picker');
      onSelect(result.fileUrl);
      onClose();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Upload mislukt', description: String(err) });
    } finally { setUploading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[75vh] flex flex-col mx-4 border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-[#202223]">
            {mediaType === 'video' ? 'Video kiezen' : 'Afbeelding kiezen'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
        </div>

        {/* Search + upload */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Zoek in media..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300" />
          </div>
          <label className="inline-flex items-center gap-2 bg-[#202223] text-white px-3.5 py-2 rounded-lg text-[13px] font-medium cursor-pointer hover:bg-[#333] shrink-0">
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploaden...' : 'Upload'}
            <input type="file" accept={mediaType === 'video' ? 'video/*' : 'image/*'} onChange={(e) => e.target.files && handleUpload(e.target.files)} className="hidden" />
          </label>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <FolderOpen className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-[#6d7175]">Geen {mediaType === 'video' ? "video's" : 'afbeeldingen'} gevonden</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { onSelect(item.fileUrl); onClose(); }}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-[#202223] hover:ring-1 hover:ring-[#202223]/20 transition-all"
                >
                  {mediaType === 'video' ? (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center"><Video className="h-6 w-6 text-white/50" /></div>
                  ) : (
                    <img src={item.fileUrl} alt={item.altText || item.originalName} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-[10px] truncate">{item.originalName}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageFieldEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [showPicker, setShowPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleDirectUpload = async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      onChange(result.fileUrl);
    } catch (err) { toast({ variant: 'destructive', title: 'Upload mislukt', description: String(err) }); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      {value && (
        <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
          <img src={value} alt="" className="w-full h-36 object-cover" />
          <button onClick={() => onChange('')} className="absolute top-2 right-2 bg-white/90 border border-gray-200 text-gray-500 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => setShowPicker(true)} className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-[#202223] px-3 py-2 rounded-lg text-[13px] transition-colors flex-1 justify-center font-medium">
          <ImageIcon className="h-4 w-4" /> Kies uit media
        </button>
        <label className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-[#202223] px-3 py-2 rounded-lg text-[13px] cursor-pointer transition-colors flex-1 justify-center font-medium">
          <Upload className="h-4 w-4" /> {uploading ? 'Uploaden...' : 'Upload'}
          <input type="file" accept="image/*" onChange={(e) => e.target.files && handleDirectUpload(e.target.files)} className="hidden" />
        </label>
      </div>
      <input type="url" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Of plak een URL..." className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-[#6d7175] outline-none focus:border-gray-300" />
      <MediaPickerModal isOpen={showPicker} onClose={() => setShowPicker(false)} onSelect={onChange} mediaType="image" />
    </div>
  );
}

function VideoFieldEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [showPicker, setShowPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleDirectUpload = async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      onChange(result.fileUrl);
    } catch (err) { toast({ variant: 'destructive', title: 'Upload mislukt', description: String(err) }); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      {value && (
        <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
          <video src={value} className="w-full h-28 object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center"><Video className="h-6 w-6 text-white/50" /></div>
          <button onClick={() => onChange('')} className="absolute top-2 right-2 bg-white/90 border border-gray-200 text-gray-500 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => setShowPicker(true)} className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-[#202223] px-3 py-2 rounded-lg text-[13px] transition-colors flex-1 justify-center font-medium">
          <Video className="h-4 w-4" /> Kies uit media
        </button>
        <label className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-[#202223] px-3 py-2 rounded-lg text-[13px] cursor-pointer transition-colors flex-1 justify-center font-medium">
          <Upload className="h-4 w-4" /> {uploading ? 'Uploaden...' : 'Upload'}
          <input type="file" accept="video/*" onChange={(e) => e.target.files && handleDirectUpload(e.target.files)} className="hidden" />
        </label>
      </div>
      <input type="url" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Of plak een video URL..." className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-[#6d7175] outline-none focus:border-gray-300" />
      <MediaPickerModal isOpen={showPicker} onClose={() => setShowPicker(false)} onSelect={onChange} mediaType="video" />
    </div>
  );
}

function FieldEditor({ field, value, onChange }: { field: FieldConfig; value: string; onChange: (value: string) => void }) {
  const inputClass = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300 transition-colors";

  switch (field.type) {
    case 'textarea':
    case 'richtext':
      return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || field.defaultValue} rows={field.type === 'richtext' ? 6 : 3} className={`${inputClass} resize-y`} />;
    case 'image':
      return <ImageFieldEditor value={value} onChange={onChange} />;
    case 'video':
      return <VideoFieldEditor value={value} onChange={onChange} />;
    case 'url':
      return <input type="url" value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || 'https://...'} className={inputClass} />;
    case 'color':
      return (
        <div className="flex gap-2 items-center">
          <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 border border-gray-200 rounded-lg cursor-pointer p-0.5" />
          <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="#000000" className={`flex-1 ${inputClass}`} />
        </div>
      );
    default:
      return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || field.defaultValue} className={inputClass} />;
  }
}

function SectionEditor({ section, pageSlug, getContent, onFieldChange, expandedSections, toggleSection, savingKeys }: {
  section: SectionConfig;
  pageSlug: string;
  getContent: (key: string, defaultValue: string) => string;
  onFieldChange: (key: string, value: string, field: FieldConfig) => void;
  expandedSections: Set<string>;
  toggleSection: (id: string) => void;
  savingKeys: Set<string>;
}) {
  const isExpanded = expandedSections.has(section.id);

  return (
    <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 overflow-hidden">
      <button
        onClick={() => toggleSection(section.id)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-[#202223]">{section.label}</h3>
            {section.description && <p className="text-xs text-[#6d7175]">{section.description}</p>}
          </div>
        </div>
        <span className="text-xs text-[#6d7175]">{section.fields.length} velden</span>
      </button>

      {isExpanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {section.fields.map((field) => {
            const currentValue = getContent(field.key, field.defaultValue);
            return (
              <div key={field.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-[#202223]">{field.label}</label>
                  <div className="flex items-center gap-1.5">
                    {savingKeys.has(field.key) && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                    <span className="text-[10px] text-[#6d7175] font-mono">{field.key}</span>
                  </div>
                </div>
                {field.helpText && <p className="text-xs text-[#6d7175] mb-1.5">{field.helpText}</p>}
                <FieldEditor field={field} value={currentValue} onChange={(v) => onFieldChange(field.key, v, field)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CmsPageEditor() {
  const { pageSlug = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isExpertiseDetail = pageSlug.startsWith('expertise-');
  const pageConfig = isExpertiseDetail
    ? getExpertiseDetailConfig(pageSlug.replace('expertise-', ''))
    : getPageConfig(pageSlug);

  const { getContent, loading } = useCmsContent(pageSlug);
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hero']));

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const getEffectiveContent = useCallback((key: string, defaultValue: string): string => {
    if (key in localValues) return localValues[key];
    return getContent(key, defaultValue);
  }, [localValues, getContent]);

  const saveTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  const handleFieldChange = useCallback((key: string, value: string, field: FieldConfig) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
    if (saveTimeouts.current[key]) clearTimeout(saveTimeouts.current[key]);
    saveTimeouts.current[key] = setTimeout(async () => {
      try {
        setSavingKeys((prev) => new Set(prev).add(key));
        const contentType = field.type === 'richtext' ? 'html' : field.type === 'image' ? 'image' : 'text';
        await updatePageContent(pageSlug, key, value, contentType);
        queryClient.invalidateQueries(['page-content', pageSlug]);
      } catch (err) {
        toast({ variant: 'destructive', title: 'Opslaan mislukt', description: String(err) });
      } finally {
        setSavingKeys((prev) => { const next = new Set(prev); next.delete(key); return next; });
      }
    }, 800);
  }, [pageSlug, queryClient, toast]);

  const refreshPreview = () => {
    if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
  };

  if (!pageConfig) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center bg-[#f6f6f7]">
            <div className="text-center">
              <p className="text-sm text-[#6d7175] mb-3">Pagina "{pageSlug}" niet gevonden</p>
              <button onClick={() => navigate('/admin/cms')} className="text-[13px] font-medium text-[#202223] hover:underline">Terug naar overzicht</button>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-hidden bg-[#f6f6f7] flex flex-col">
          {/* Top bar */}
          <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
              <button onClick={() => navigate('/admin/cms')} className="text-gray-400 hover:text-[#202223] p-1"><ArrowLeft className="h-4 w-4" /></button>
              <div>
                <h1 className="text-sm font-semibold text-[#202223]">{pageConfig.name}</h1>
                <p className="text-xs text-[#6d7175]">{pageConfig.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {savingKeys.size > 0 && (
                <div className="flex items-center gap-1.5 text-[13px] text-[#6d7175] mr-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Opslaan...</span>
                </div>
              )}
              <button onClick={refreshPreview} className="p-2 text-gray-400 hover:text-[#202223] rounded-lg hover:bg-gray-50" title="Preview vernieuwen">
                <RefreshCw className="h-4 w-4" />
              </button>
              <a href={pageConfig.path} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-[#202223] rounded-lg hover:bg-gray-50" title="Open pagina">
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Split screen */}
          <div className="flex-1 flex overflow-hidden">
            {/* Preview */}
            <div className="hidden lg:flex flex-1 bg-[#f6f6f7] p-4 items-stretch">
              <div className="flex-1 bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden border border-gray-200/60">
                <iframe ref={iframeRef} src={pageConfig.path} className="w-full h-full border-0" title={`Preview: ${pageConfig.name}`} />
              </div>
            </div>

            {/* Editor panel */}
            <div className="w-full lg:w-[420px] xl:w-[460px] border-l border-gray-200 bg-[#f6f6f7] overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : (
                pageConfig.sections.map((section) => (
                  <SectionEditor
                    key={section.id}
                    section={section}
                    pageSlug={pageSlug}
                    getContent={getEffectiveContent}
                    onFieldChange={handleFieldChange}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    savingKeys={savingKeys}
                  />
                ))
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
